import React, {useCallback, useEffect, useState} from "react";
import {
  ComboBox,
  Spinner,
  SpinnerSize,
  type IComboBox,
  type IComboBoxOption,
  type IComboBoxStyles,
} from "@fluentui/react";

import useEditableHook from "./useEditableHook";
import type ISelectedProps from "./ISelectedProps";

const comboBoxStyles: Partial<IComboBoxStyles> = {
  root: {
    maxWidth: 100,
    maxHeight: 100,
    marginRight: 5,
  },
  callout: {width: "fit-content"},
};
const Select: React.FunctionComponent<ISelectedProps> = ({
  value,
  constructionElementsName,
  property,
  extraInfo,
  isDisabled,
  onValueChanged,
  onDropdownClosed,
}) => {
  const [options, setOptions] = useState<IComboBoxOption[]>([]);  

  const [loading, IsLoading] = useState<boolean>();
  const {getConstructionPropertyItemsFromMatRef} = useEditableHook();

  const onCheckboxChanged = (
    _: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
    __?: number,
    selectedValue?: string,
  ): void => {
    if (option !== undefined) {
      onValueChanged(option.text);
    } else {
      onValueChanged(selectedValue);
    }
  };

  const handleDropdownClosure = (): void => {
    onDropdownClosed();
  };

  const getOptions = useCallback(async (): Promise<void> => {
    const setFilter = (): string => {
      switch (constructionElementsName) {
        case "devices":
        case "masts":
          return extraInfo?.family ?? "";
        default:
          return "";
      }
    };

    const constructionPropertyItems = await getConstructionPropertyItemsFromMatRef(constructionElementsName, property, setFilter());
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (constructionPropertyItems?.items) {
      setOptions(constructionPropertyItems.items.map((opt: string) => ({key: opt, text: opt})));
    }
    IsLoading(false);
  }, [extraInfo, getConstructionPropertyItemsFromMatRef, constructionElementsName, property]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    IsLoading(true);
    void getOptions();
  }, [getOptions]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <div style={{display: "flex"}}>
      <ComboBox
        onMenuDismissed={handleDropdownClosure}
        selectedKey={String(value ?? "")}
        options={options}
        styles={comboBoxStyles}
        allowFreeform={false}
        autoComplete="on"
        onChange={onCheckboxChanged}
        disabled={isDisabled}
      />
      {loading === true && <Spinner size={SpinnerSize.small} ariaLive="assertive" />}
    </div>
  );
};

export default Select;
